import type { Media } from '@fitting-room/contracts'
import { assetUrl } from './api'

/**
 * Which single picture to show for a thing, and at which derivative.
 *
 * `position` is the operator's own ordering, so the first one is the shot the
 * boutique chose to lead with — never the first one the ingest happened to
 * write. `variants` is keyed by width as a string, and the pick is the
 * narrowest that still holds up on a 3x phone, falling back to the widest that
 * exists: an item whose only derivative is 320px is better shown soft than not
 * shown at all.
 *
 * Shared rather than copied, because a card in the deck and an option on a
 * question screen are asking the same question of the same data, and two
 * answers to it is how one surface quietly starts serving thumbnails.
 */
export function bestImage(media: readonly Media[]): { src: string; alt: string } | null {
  const first = [...media].sort((a, b) => a.position - b.position)[0]
  if (!first) return null
  const variants = first.variants
  if (variants) {
    const widths = Object.keys(variants)
      .map((w) => Number(w))
      .filter((w) => Number.isFinite(w))
      .sort((a, b) => a - b)
    const chosen = widths.find((w) => w >= 640) ?? widths[widths.length - 1]
    const url = chosen === undefined ? undefined : variants[String(chosen)]
    if (url) return { src: assetUrl(url), alt: first.alt ?? '' }
  }
  return { src: assetUrl(first.url), alt: first.alt ?? '' }
}

/**
 * The same first picture, for a frame a quarter of the screen: every
 * derivative the pipeline made, as a `srcset`, so the browser takes the
 * narrowest that holds up at this phone's density rather than this file
 * guessing one.
 *
 * `bestImage` picks one width for a frame the size of the phone, where the
 * answer is always "640 or more". A grid card is ~170px wide: 320 on a 1x
 * screen, 640 on a 2x or 3x one, and never the 1280 — which is the bandwidth
 * the grid exists to save. `src` is the narrowest that exists, for a browser
 * that ignores `srcset`.
 *
 * A picture with no derivatives — the fixture catalogue, and every import that
 * landed originals only — has nothing to choose between, so it comes back as
 * its one URL and no `srcSet` at all.
 */
export function cardImage(media: readonly Media[]): { src: string; srcSet?: string; alt: string } | null {
  const first = [...media].sort((a, b) => a.position - b.position)[0]
  if (!first) return null
  const alt = first.alt ?? ''
  const widths = Object.entries(first.variants ?? {})
    .map(([w, url]) => ({ width: Number(w), url }))
    .filter((v) => Number.isFinite(v.width) && v.width > 0 && v.url !== '')
    .sort((a, b) => a.width - b.width)
  const narrowest = widths[0]
  if (!narrowest) return { src: assetUrl(first.url), alt }
  return {
    src: assetUrl(narrowest.url),
    srcSet: widths.map((v) => `${assetUrl(v.url)} ${v.width}w`).join(', '),
    alt,
  }
}
